import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, FolderPlus, RefreshCw, ExternalLink, HardDrive, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { api, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const Row = ({ ok, label }) => (
  <div className="flex items-center gap-2 py-1 text-sm">
    {ok ? (
      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
    ) : (
      <XCircle className="h-4 w-4 shrink-0 text-crimson" />
    )}
    <span className={ok ? "text-foreground" : "text-muted-foreground"}>{label}</span>
  </div>
);

export default function Settings() {
  const { config, refreshConfig, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: s }, { data: l }] = await Promise.all([
        api.get("/drive/status"),
        api.get("/activity"),
      ]);
      setStatus(s);
      setLogs(l);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => { load(); }, []);

  const setup = async () => {
    setBusy(true);
    try {
      await api.post("/drive/setup");
      toast.success("Mevcut Drive klasörleri ve Google Sheet doğrulandı.");
      await load();
      await refreshConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaynaklar doğrulanamadı.", { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const connect = async () => {
    try {
      const { data } = await api.get("/auth/google/login");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Google bağlantısı başlatılamadı.");
    }
  };

  const folders = status?.folders || {};

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Ayarlar</h1>
        <p className="mt-1 text-sm text-muted-foreground">Hesap, Google Drive bağlantısı ve işlem kaydı.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg font-bold tracking-tight">Hesap</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm font-semibold text-foreground">{user?.name}</p>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
            <HardDrive className="h-4 w-4" /> Google Drive
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-xl bg-muted/60 p-4">
            <Row ok={config?.google_configured} label="Google OAuth anahtarları sunucuda tanımlı" />
            <Row ok={status?.connected} label={`Drive hesabı bağlı${status?.email ? ` (${status.email})` : ""}`} />
            <Row ok={!!folders.root} label="SHB Evrak Takip klasörü" />
            <Row ok={!!folders["inbox_Adliye"]} label="01 - Adliye Gelen Evraklar" />
            <Row ok={!!folders["inbox_TÜRKPATENT"]} label="02 - TÜRKPATENT Gelen Evraklar" />
            <Row ok={!!folders["inbox_Diğer"]} label="04 - Diğer Gelen Evraklar" />
            <Row ok={!!folders.done} label="03 - Tamamlanan Evraklar (Adliye / TÜRKPATENT / Diğer)" />
            <Row ok={!!folders.spreadsheet} label="SHB Evrak Takip Kayıtları (ISLER / AYARLAR)" />
          </div>

          {!!status?.missing?.length && (
            <div className="rounded-xl border border-crimson/30 bg-crimson/5 p-4 text-sm" data-testid="missing-resources">
              <p className="font-semibold text-crimson">Erişilemeyen kaynaklar</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                {status.missing.map((m) => <li key={m}>{m}</li>)}
              </ul>
              <p className="mt-2 text-xs text-muted-foreground">
                Kaynaklar otomatik oluşturulmaz. Klasör kimliklerini ve paylaşım izinlerini kontrol edin.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={connect} disabled={!config?.google_configured} data-testid="drive-auth-btn"
              className="bg-navy text-white hover:bg-navy/90 dark:bg-slate-100 dark:text-navy">
              {status?.connected ? "Bağlantıyı Yenile" : "Google Drive'a Bağlan"}
            </Button>
            <Button variant="outline" onClick={setup} disabled={busy || !status?.connected} data-testid="drive-setup-btn">
              <FolderPlus className={cn("h-4 w-4", busy && "animate-spin")} /> Kaynakları Doğrula
            </Button>
            {folders.spreadsheet && (
              <Button variant="outline" asChild data-testid="open-sheet-btn">
                <a href={`https://docs.google.com/spreadsheets/d/${folders.spreadsheet}`} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-4 w-4" /> Kayıt Tablosu
                </a>
              </Button>
            )}
            <Button variant="ghost" onClick={load} data-testid="settings-refresh-btn">
              <RefreshCw className="h-4 w-4" /> Durumu Yenile
            </Button>
          </div>

          {!config?.google_configured && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm dark:border-amber-500/30 dark:bg-amber-500/10" data-testid="oauth-setup-help">
              <p className="font-semibold text-foreground">Google OAuth kurulum adımları</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-muted-foreground">
                <li>console.cloud.google.com adresinde yeni bir proje açın.</li>
                <li>APIs &amp; Services → Library: <strong>Google Drive API</strong> ve <strong>Google Sheets API</strong>'yi etkinleştirin.</li>
                <li>OAuth consent screen → External; test kullanıcısı olarak {user?.email || "kendi Gmail adresinizi"} ekleyin.</li>
                <li>Credentials → Create OAuth client ID → Web application.</li>
                <li>Authorized redirect URI:
                  <code className="ml-1 break-all rounded bg-background px-1.5 py-0.5 font-mono text-xs">{config?.redirect_uri}</code>
                </li>
                <li>Client ID ve Client Secret değerlerini bana iletin; sunucu ortamına güvenli şekilde eklerim.</li>
              </ol>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
            <Activity className="h-4 w-4" /> İşlem Kaydı
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!logs.length && (
            <p className="text-sm text-muted-foreground" data-testid="activity-empty">Henüz işlem kaydı yok.</p>
          )}
          <div data-testid="activity-list">
            {logs.map((l, i) => (
              <div key={l.id}>
                {i > 0 && <Separator />}
                <div className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                  <span className="text-foreground">{l.detail}</span>
                  <span className="font-mono text-xs text-muted-foreground">{l.type} · {formatDate(l.at)}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
